import usePaginatedSource from "@/hooks/usePaginatedSource";
import React, {useEffect, useState} from "react";
import toast from "react-hot-toast";
import moment from "moment/moment";
import PaginationControl from "@/Components/PaginationControl";
import {WorkflowExecution, WorkflowExecutionSummary} from "@/types/workflow";
import WorkflowExecutionModal from "./WorkflowExecutionModal";
import {renderStatusBadge} from "@/Pages/Workflow/Components/StatusBadge";
import Spinner from "@/Components/Spinner";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClockRotateLeft } from '@fortawesome/free-solid-svg-icons';

export default function () {
    const paginatedSource = usePaginatedSource<WorkflowExecutionSummary>('workflow-executions.list');
    const [initialized, setInitialized] = useState(false);
    const [selectedExecutionId, setSelectedExecutionId] = useState<number | null>(null);
    const [showModal, setShowModal] = useState(false);

    const {
        items,
        pagination,
        error,
        total,
        isLoading
    } = paginatedSource;

    useEffect(() => {
        if (error) {
            toast.error('Failed to load executions');
        }
    }, [error]);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const execId = urlParams.get('id');
        if (execId) {
            const executionId = parseInt(execId, 10);
            if (!isNaN(executionId)) {
                setSelectedExecutionId(executionId);
                setShowModal(true);
            }
        }
    }, []);

    useEffect(() => {
        if (!initialized) {
            setInitialized(true);
        } else {
            const rafId = requestAnimationFrame(() => {
                window.scrollTo({top: 0, behavior: 'smooth'});
            });
            return () => cancelAnimationFrame(rafId);
        }
    }, [pagination.currentPage]);

    const handleRowClick = (execution: WorkflowExecutionSummary) => {
        setSelectedExecutionId(execution.id);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedExecutionId(null);
    };

    if (total === 0) {
        return (
            <div className="space-y-4">
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                    <div className="text-center py-16">
                        {isLoading ? (
                            <Spinner className="w-4 h-4" />
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faClockRotateLeft} className="text-gray-300 dark:text-gray-600 text-6xl mb-4" />
                                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No executions yet</h3>
                                <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                                    Your workflow run history will appear here.
                                </p>
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                <div className="flex flex-col">
                    <div className="overflow-x-auto">
                        <div className="inline-block min-w-full align-middle">
                            <table className="w-full leading-normal table-auto text-base">
                                <thead>
                                <tr>
                                    <th className="px-5 py-3 border-b bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-left text-sm  text-gray-500 dark:text-gray-400 md:uppercase md:tracking-wider">
                                        Started
                                    </th>
                                    <th className="px-5 py-3 border-b bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-left text-sm  text-gray-500 dark:text-gray-400 md:uppercase md:tracking-wider">
                                        Workflow
                                    </th>
                                    <th className="px-5 py-3 border-b bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-left text-sm  text-gray-500 dark:text-gray-400 md:uppercase md:tracking-wider">
                                        status
                                    </th>
                                    <th className="px-5 py-3 border-b bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-left text-sm  text-gray-500 dark:text-gray-400 md:uppercase md:tracking-wider">
                                        Files
                                    </th>

                                </tr>
                                </thead>
                                <tbody>
                                {items.map((execution, idx) => (
                                    <tr
                                        key={execution.id}
                                        onClick={() => handleRowClick(execution)}
                                        className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                                    >
                                        <td className={`px-5 py-5 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 ${idx < items.length - 1 ? 'border-b border-gray-200 dark:border-gray-700' : ''}`}>
                                            {moment(execution.started_at || execution.created_at).format('MMM DD, YYYY h:mm:ss A')}
                                        </td>
                                        <td className={`px-5 py-5 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 ${idx < items.length - 1 ? 'border-b border-gray-200 dark:border-gray-700' : ''}`}>
                                            {execution.workflow_name}
                                        </td>
                                        <td className={`px-5 py-5 text-sm ${idx < items.length - 1 ? 'border-b border-gray-200 dark:border-gray-700' : ''}`}>
                                            {renderStatusBadge(execution.status)}
                                        </td>
                                        <td className={`px-5 py-5 text-sm text-gray-900 dark:text-gray-100 ${idx < items.length - 1 ? 'border-b border-gray-200 dark:border-gray-700' : ''}`}>
                                            <span>{execution.total_extraction_jobs}</span>
                                        </td>

                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="border-t border-gray-200 dark:border-gray-700">
                        <div className="flex justify-between items-center p-4">
                            <PaginationControl source={paginatedSource}/>
                        </div>
                    </div>
                </div>
            </div>

            <WorkflowExecutionModal
                show={showModal}
                onClose={handleCloseModal}
                executionId={selectedExecutionId}
            />
        </div>
    );
}
